// ============================================================================
// FSA REPORT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaReportService } from 'api/services/fsa/fsaReportService';
import type { ID } from 'api/types';
import type { ReportHydrate, ReportListItem } from 'api/types/fsa/reportData.types';

export const useFsaReportsList = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) { setData([]); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaReportService.list(engagementId);
      setData(r.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

export const useFsaReportHydrate = (
  engagementId: ID | null | undefined,
  reportId: ID | null | undefined,
) => {
  const [data, setData] = useState<ReportHydrate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId || !reportId) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaReportService.hydrate(engagementId, reportId);
      setData(r.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId, reportId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};
